import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Typography,
  Tab,
  Tabs,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
} from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { DashboardService } from '../services/DashboardService';
import PriceAdjustmentCalculator from './PriceAdjustmentCalculator';

function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index} style={{ width: '100%' }}>
      {value === index && <Box p={3}>{children}</Box>}
    </div>
  );
}

function ExecutiveSummary({ data, loading }) {
  if (loading) return <CircularProgress />;
  if (!data) return <Alert severity="error">Pas de données</Alert>;

  const summary = data.summary || {};
  const recommendations = data.recommendations || [];
  const topCompetitor = summary.topCompetitor || {};
  const rarityBreakdown = summary.rarityBreakdown || [];
  const rarityColors = {
    TOP: '#2e7d32',
    Rare: '#558b2f',
    Commun: '#f9a825',
    'Tres Commun': '#ef6c00',
    Trash: '#c62828',
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Ajustements de prix nécessaires
            </Typography>
            <Typography variant="h5">
              {summary.priceAdjustmentsNeeded || 0}
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Opportunités du marché
            </Typography>
            <Typography variant="h5" color={summary.marketOpportunitiesDetected && summary.marketOpportunitiesDetected > 0 ? 'green' : 'orange'}>
              {summary.marketOpportunitiesDetected || 0}
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Concurrents suivis
            </Typography>
            <Typography variant="h5">
              {summary.activeCompetitorsTracked || 0}
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Meilleur concurrent
            </Typography>
            <Typography variant="h6">
              {topCompetitor.name || 'N/A'}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {topCompetitor.listings || 0} references suivies
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Card>
          <CardHeader title="Répartition des refOEM par rareté" />
          <CardContent>
            {rarityBreakdown.length === 0 ? (
              <Typography color="textSecondary">Aucune donnée de rareté disponible</Typography>
            ) : (
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={rarityBreakdown} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" />
                  <YAxis allowDecimals={false} />
                  <Tooltip formatter={(value) => [`${value}`, 'refOEM']} />
                  <Bar dataKey="count" radius={[8, 8, 0, 0]}>
                    {rarityBreakdown.map((entry) => (
                      <Cell key={entry.label} fill={rarityColors[entry.label] || '#1976d2'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Card>
          <CardHeader title="Top recommandations de prix" />
          <CardContent>
            {recommendations.length === 0 ? (
              <Typography color="textSecondary">Aucune recommandation disponible</Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>OEM</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell align="right">Votre prix</TableCell>
                      <TableCell align="right">Médiane marché</TableCell>
                      <TableCell align="right">Ajustement</TableCell>
                      <TableCell>Priorité</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recommendations.slice(0, 5).map((rec, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{rec.oem || 'N/A'}</TableCell>
                        <TableCell>{rec.type || 'N/A'}</TableCell>
                        <TableCell align="right">{rec.yourPrice !== undefined ? `${rec.yourPrice.toFixed(2)}€` : 'N/A'}</TableCell>
                        <TableCell align="right">{rec.marketMedian !== undefined ? `${rec.marketMedian.toFixed(2)}€` : 'N/A'}</TableCell>
                        <TableCell align="right" style={{ fontWeight: 'bold', color: rec.adjustment > 0 ? 'red' : 'green' }}>
                          {rec.adjustment !== undefined ? `${rec.adjustment > 0 ? '+' : ''}${rec.adjustment.toFixed(2)}%` : 'N/A'}
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            style={{
                              color: rec.priority === 'HIGH' ? 'red' : rec.priority === 'MEDIUM' ? 'orange' : 'green',
                              fontWeight: 'bold'
                            }}
                          >
                            {rec.priority || 'N/A'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
}

function Opportunities({ data, loading }) {
  if (loading) return <CircularProgress />;
  if (!data || !Array.isArray(data) || data.length === 0) return <Alert severity="info">Pas d'opportunités détectées (aucune OEM absente du stock avec &gt; 20 ventes)</Alert>;

  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow style={{ backgroundColor: '#f5f5f5' }}>
            <TableCell>OEM absente</TableCell>
            <TableCell align="right">Ventes totales</TableCell>
            <TableCell align="right">Prix moyen</TableCell>
            <TableCell align="right">Vendeurs</TableCell>
            <TableCell>Détails</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((item, idx) => (
            <TableRow key={idx} style={{ backgroundColor: '#fffacd' }}>
              <TableCell style={{ fontWeight: 'bold' }}>{item.oem}</TableCell>
              <TableCell align="right">{item.total_sales || 0}</TableCell>
              <TableCell align="right">{(item.avg_price || 0).toFixed(2)}€</TableCell>
              <TableCell align="right">{item.vendor_count || 0}</TableCell>
              <TableCell>{item.recommendation || item.vendors || 'N/A'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function DismantlingPriority({ data, loading }) {
  if (loading) return <CircularProgress />;
  if (!data || !Array.isArray(data) || data.length === 0) return <Alert severity="info">Aucun libellé vendu absent du stock</Alert>;

  return (
    <Box>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow style={{ backgroundColor: '#f5f5f5' }}>
              <TableCell>Rang</TableCell>
              <TableCell>Libellé</TableCell>
              <TableCell align="right">Ventes estimées</TableCell>
              <TableCell align="right">Vendeurs</TableCell>
              <TableCell>Dernière vente</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((item, idx) => (
              <TableRow key={idx}>
                <TableCell>{idx + 1}</TableCell>
                <TableCell>{item.libelle || 'Inconnu'}</TableCell>
                <TableCell align="right">{item.sold_count || 0}</TableCell>
                <TableCell align="right">{item.seller_count || 0}</TableCell>
                <TableCell>{item.last_sold_date || 'Date inconnue'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

function CompetitorActivity({ data, loading, daysFilter, onDaysFilterChange }) {
  if (loading) return <CircularProgress />;
  if (!data || data.length === 0) return <Alert severity="info">Aucune activité détectée</Alert>;

  return (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
        <Select
          value={daysFilter}
          onChange={(event) => onDaysFilterChange(event.target.value)}
          size="small"
        >
          <MenuItem value={7}>7 jours</MenuItem>
          <MenuItem value={30}>30 jours</MenuItem>
          <MenuItem value={90}>90 jours</MenuItem>
          <MenuItem value={180}>180 jours</MenuItem>
        </Select>
      </Box>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
            <TableRow>
              <TableCell><strong>Vendeur</strong></TableCell>
              <TableCell><strong>Date Analyse</strong></TableCell>
              <TableCell align="right"><strong>OEM observées</strong></TableCell>
              <TableCell align="right"><strong>Nouvelles annonces</strong></TableCell>
              <TableCell align="right"><strong>Ventes estimées</strong></TableCell>
              <TableCell align="right"><strong>Var. prix moyenne</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((comp, idx) => (
              <TableRow key={idx}>
                <TableCell><strong>{comp.seller_name}</strong></TableCell>
                <TableCell>{comp.scraped_at || 'Jamais'}</TableCell>
                <TableCell align="right">{comp.oem_count}</TableCell>
                <TableCell align="right">{comp.new_listings || 0}</TableCell>
                <TableCell align="right">{comp.estimated_sales ?? comp.disappearances ?? 0}</TableCell>
                <TableCell align="right">
                  {comp.avg_price_variation ? `${parseFloat(comp.avg_price_variation).toFixed(1)}%` : '0%'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export default function Dashboard() {
  const [tabValue, setTabValue] = useState(0);
  const [competitorDaysFilter, setCompetitorDaysFilter] = useState(30);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState({
    executive: null,
    priceAdjustment: null,
    opportunities: null,
    dismantling: null,
    competitorActivity: null,
  });

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);
      try {
        const [exec, price, opp, dism, competitorActivity] = await Promise.all([
          DashboardService.getExecutiveSummary().catch((e) => { console.error('getExecutiveSummary error:', e); return {}; }),
          DashboardService.getPriceAdjustment().catch((e) => { console.error('getPriceAdjustment error:', e); return []; }),
          DashboardService.getOpportunities().catch((e) => { console.error('getOpportunities error:', e); return []; }),
          DashboardService.getMissingStockLibelles().catch((e) => { console.error('getMissingStockLibelles error:', e); return []; }),
          DashboardService.getCompetitorActivity(competitorDaysFilter).catch((e) => { console.error('getCompetitorActivity error:', e); return []; }),
        ]);

        setData({
          executive: exec || {},
          priceAdjustment: price || [],
          opportunities: opp || [],
          dismantling: dism || [],
          competitorActivity: competitorActivity || [],
        });
      } catch (loadError) {
        console.error('Error loading dashboard data:', loadError);
        setError(loadError.message || 'Une erreur est survenue lors du chargement des données');
      } finally {
        setLoading(false);
      }
    };

    loadData();

    const interval = setInterval(loadData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [competitorDaysFilter]);

  return (
    <Container maxWidth="lg" style={{ paddingTop: 20 }}>
      <Typography variant="h4" gutterBottom style={{ marginBottom: 30, fontWeight: 'bold' }}>
        Tableau de bord
      </Typography>

      {error && (
        <Alert severity="error" style={{ marginBottom: 20 }}>
          {error}
        </Alert>
      )}

      <Paper style={{ marginBottom: 20 }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
          <Tab label="Resume" />
          <Tab label="Ajustement des prix" />
          <Tab label="Opportunités OEM" />
          <Tab label="Optimisation démontages" />
          <Tab label="Activité concurrents" />
        </Tabs>
      </Paper>

      <Box style={{ marginBottom: 30 }}>
        <TabPanel value={tabValue} index={0}>
          <ExecutiveSummary data={data.executive} loading={loading} />
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <PriceAdjustmentCalculator />
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <Opportunities data={data.opportunities} loading={loading} />
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          <DismantlingPriority data={data.dismantling} loading={loading} />
        </TabPanel>

        <TabPanel value={tabValue} index={4}>
          <CompetitorActivity
            data={data.competitorActivity}
            loading={loading}
            daysFilter={competitorDaysFilter}
            onDaysFilterChange={setCompetitorDaysFilter}
          />
        </TabPanel>
      </Box>
    </Container>
  );
}
